import * as React from 'react';
import { useEffect, useCallback } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import MainHeader from '../../../headers/MainHeader/MainHeader';
import * as actions from '../../../../redux/organization/actions.organization';
import OrganizationHeader from '../OrganizationHeader/OrganizationHeader';
import { IOrganization, IOrganizationMember, IStore } from '../../../../redux/initialState';
import { confirmAlert } from '../../../../services/mini/alanConfirmAlert';
import AddOrganizationMemberForm from './AddOrganizationMemberForm/AddOrganizationMemberForm';
import ConfirmPopup from '../../../components/ConfirmPopup/ConfirmPopup';
import AddEditOrganizationNameForm from './AddEditOrganizationNameForm/AddEditOrganizationNameForm';
import { Loader, LoaderSize, LoaderTheme } from '../../../components/Loader/Loader';
import Overlay from '../../../components/Overlay/Overlay';
import formats from '../../../../const/formats';
import getPageTitle from '../../../../services/mini/getPageTitle';
import * as moment from 'moment';
import asyncDispatch from '../../../../services/mini/asyncDispatch';


type OrganizationPageProps = {
    org: IOrganization | null,
} & PropsFromRedux;

function OrganizationPage({
    createOrgAvailable,
    memberOf,
    email,
    org,
    createOrganization,
    updateOrganization,
    removeOrganization,
    addOrganizationMember,
    removeOrganizationMember,
    isUpdating
}: OrganizationPageProps) {

    const [members, setMembrs] = React.useState(org?.members || []);

    useEffect(() => {
        document.title = getPageTitle('Organization');
    });

    useEffect(() => {
        setMembrs(org?.members || []);
    }, [org]);

    const addMemberCb = () => {
        confirmAlert({
            customUI: ({ onClose }) => {
                return (
                    <AddOrganizationMemberForm
                        userEmail={email}
                        addMember={(email) => {
                            addOrganizationMember({ email });
                        }}
                        onClose={onClose}
                    />
                );
            }
        });
    };

    const removeMemberCb = (email) => {
        removeOrganizationMember({ email });
    };

    const removeOrganizationCb = () => {
        confirmAlert({
            customUI: ({ onClose }) => {
                return (
                    <ConfirmPopup
                        onClose={onClose}
                        okBtnLabel="Delete"
                        okBtnCb={() => {
                            onClose();
                            removeOrganization();
                        }}
                        title="Delete"
                        message={<div>Are you sure you want to delete <b>{org.name}</b>?</div>}
                    />
                );
            }
        });

    };
    const addEditOrganizationCb = () => {
        confirmAlert({
            customUI: ({ onClose }) => {
                return (
                    <AddEditOrganizationNameForm
                        organizationName={org?.name || ''}
                        okAction={(organizationName) => {
                            if (org === null) {
                                createOrganization({ name: organizationName });
                            } else {
                                updateOrganization({ name: organizationName });
                            }
                        }}
                        onClose={onClose}
                    />
                );
            }
        });
    };

    const hasMembers = org?.members?.length > 0;
    const canCreateOrg = createOrgAvailable && !memberOf;


    return (
        <div className={`organization-page`}>
            <MainHeader>
                <OrganizationHeader />
            </MainHeader>
            <div className="organization-page__body">
                {isUpdating && <Overlay>
                    <Loader position="in-center" theme={LoaderTheme.Light} size={LoaderSize.Big} />
                </Overlay>}
                <div className="organization-page__form">
                    <div className="form-sub-header">
                        Organization {(!org?.name && canCreateOrg) && <div onClick={addEditOrganizationCb} className="action-btn middle">Create</div>}
                    </div>
                    {!canCreateOrg ? <div className="organization-page__info" style={{ display: 'block' }}>
                        {memberOf ? <>You are a member of the <b>{memberOf}</b> organization.
                            <br />
                            <br />
                            <div onClick={() => {
                                confirmAlert({
                                    customUI: ({ onClose }) => {
                                        return (
                                            <ConfirmPopup
                                                onClose={onClose}
                                                okBtnLabel="Leave"
                                                okBtnCb={() => {
                                                    onClose();

                                                    removeMemberCb(email);
                                                }}
                                                title="Leave organization"
                                                message={<div>Are you sure you want to leave the <b>{memberOf}</b> organization?</div>}
                                            />
                                        );
                                    }
                                });
                            }} className="action-btn middle">Leave</div>
                        </> : <></>}
                        {!memberOf && !createOrgAvailable ? <>
                            Organization is not available on your plan. Please contact <a href="mailto:sales@alan.app"
                                target="_blank" rel="noreferrer"> sales@alan.app </a>  to get access to the organization and start collaborating in Alan AI Studio</> : <></>}
                    </div> : <>
                        <div>
                            {org?.name ?
                                <div className="organization-page__org-name">
                                    {org?.name}
                                    <div className="organization-page__org-actions">
                                        <i onClick={() => { addEditOrganizationCb(); }} title="Rename organization" className="icon icon-alan-edit" />
                                        <i
                                            onClick={() => {
                                                if (!hasMembers) {
                                                    removeOrganizationCb();
                                                }
                                            }}
                                            title={hasMembers ? "You cannot delete organization with members. Please delete members first" : "Remove organization"}
                                            className={`icon icon-alan-trash ${hasMembers ? 'disabled-with-pointer-events' : ''}`} />
                                    </div>
                                </div> :
                                <div className="organization-page__info">
                                    You currently have no organizations. Create a new organization and invite members to start collaborating in Alan AI Studio.
                                </div>
                            }
                        </div>
                        <br />
                        {org !== null && <>
                            <br />
                            <br />
                            <br />
                            <div className="form-sub-header">
                                Members <div onClick={addMemberCb} className="action-btn middle">Invite</div>
                            </div>
                            <div className="organization-page__members-list">
                                {members?.length > 0 ? members?.map(({ email, active, createTs, approvedTs }: IOrganizationMember) => {
                                    return <div key={email} className="organization-page__members-list-item">
                                        <div>
                                            {email}
                                            <div className={`organization-page__members-list-item-status ${active ? 'approved' : ''}`}>
                                                {active === false ? <>
                                                    <i className="icon icon-alan-email" />
                                                    Invitation sent: {moment(createTs).format(formats.MMM_DD_YYYY_hh_mm_a)}
                                                </> : <>
                                                    <i className="icon icon-alan-circle-ok" />
                                                    Invitation approved: {moment(approvedTs).format(formats.MMM_DD_YYYY_hh_mm_a)}
                                                </>}
                                            </div>
                                        </div>
                                        <i onClick={() => {

                                            confirmAlert({
                                                customUI: ({ onClose }) => {
                                                    return (
                                                        <ConfirmPopup
                                                            onClose={onClose}
                                                            okBtnLabel="Delete"
                                                            okBtnCb={() => {
                                                                onClose();

                                                                removeMemberCb(email);
                                                            }}
                                                            title="Delete"
                                                            message={<div>Are you sure you want to delete the user associated with the email: <b>{`${email}`}</b> from <b>{org.name}</b>?</div>}
                                                        />
                                                    );
                                                }
                                            });


                                        }} title="Remove member from organization " className="icon icon-alan-trash" />
                                    </div>;
                                }) :
                                    <div className="organization-page__info">
                                        There are currently no members in your organization. Invite new members to start collaborating in Alan AI Studio.
                                    </div>}
                            </div>
                        </>}
                    </>}
                </div>

            </div>
        </div>
    );
}

function mapStateToProps({ user, organization }: IStore, ownProps) {
    return {
        email: user.email,
        org: user.org,
        memberOf: user.memberOf,
        isUpdating: organization.isUpdating,
        createOrgAvailable: user.capabilities.createOrgAvailable,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        createOrganization: (param: actions.ICreateOrganizationParams) => asyncDispatch(actions.createOrganization, dispatch, param),
        updateOrganization: (param: actions.IUpdateOrganizationParams) => asyncDispatch(actions.updateOrganization, dispatch, param),
        removeOrganization: () => asyncDispatch(actions.removeOrganization, dispatch),
        addOrganizationMember: (param: actions.IAddOrganizationMemberParams) => asyncDispatch(actions.addOrganizationMember, dispatch, param),
        removeOrganizationMember: (param: actions.IRemoveOrganizationMemberParams) => asyncDispatch(actions.removeOrganizationMember, dispatch, param),
    };
}

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(OrganizationPage);
